import type Lenis from 'lenis'

export interface FrameState {
    time: number
    dt: number
    scroll: number
    velocity: number
    vw: number
    vh: number
    maxScroll: number
    mouseX: number
    mouseY: number
    pointerX: number
    pointerY: number
    hasPointer: boolean
    mobile: boolean
    reducedMotion: boolean
}

export type FrameCallback = (state: FrameState) => void
export type MeasureCallback = (state: FrameState) => void

interface Subscriber {
    callback: FrameCallback
    priority: number
}

interface ScrollOptions {
    offset?: number
    duration?: number
    immediate?: boolean
    force?: boolean
    onComplete?: () => void
}

const state: FrameState = {
    time: 0,
    dt: 1 / 60,
    scroll: 0,
    velocity: 0,
    vw: 1280,
    vh: 800,
    maxScroll: 0,
    mouseX: 0,
    mouseY: 0,
    pointerX: -100,
    pointerY: -100,
    hasPointer: false,
    mobile: false,
    reducedMotion: false
}

const subscribers: Array<Subscriber> = []
const measurers = new Set<MeasureCallback>()

let lenis: Lenis | null = null
let started = false
let rafId = 0
let lastTime = 0
let needsMeasure = true
let rawMouseX = 0
let rawMouseY = 0
let resizeObserver: ResizeObserver | null = null
let motionQuery: MediaQueryList | null = null

const readScroll = (): number => {
    if (lenis) return lenis.scroll

    return window.scrollY
}

const runMeasure = (): void => {
    state.vw = window.innerWidth
    state.vh = window.innerHeight
    state.mobile = state.vw < 768
    state.maxScroll = Math.max(0, document.documentElement.scrollHeight - state.vh)
    state.scroll = readScroll()

    measurers.forEach(callback => callback(state))
}

const requestMeasure = (): void => {
    needsMeasure = true
}

const onPointerMove = (event: PointerEvent): void => {
    if (event.pointerType !== 'mouse') return

    state.pointerX = event.clientX
    state.pointerY = event.clientY
    state.hasPointer = true

    rawMouseX = (event.clientX / Math.max(1, state.vw)) * 2 - 1
    rawMouseY = (event.clientY / Math.max(1, state.vh)) * 2 - 1
}

const onPointerLeave = (): void => {
    rawMouseX = 0
    rawMouseY = 0
    state.hasPointer = false
}

const onMotionChange = (event: MediaQueryListEvent): void => {
    state.reducedMotion = event.matches
}

const tick = (now: number): void => {
    rafId = requestAnimationFrame(tick)

    const seconds = now / 1000
    const dt = lastTime ? Math.min(0.1, Math.max(0.001, seconds - lastTime)) : 1 / 60

    lastTime = seconds

    if (lenis) lenis.raf(now)

    if (needsMeasure) {
        needsMeasure = false
        runMeasure()
    }

    state.time = seconds
    state.dt = dt
    state.scroll = readScroll()
    state.velocity = lenis ? lenis.velocity : 0

    const follow = 1 - Math.exp(-5 * dt)

    state.mouseX += (rawMouseX - state.mouseX) * follow
    state.mouseY += (rawMouseY - state.mouseY) * follow

    for (let index = 0; index < subscribers.length; index++) {
        subscribers[index].callback(state)
    }
}

export const engine = {
    state,

    start(): void {
        if (started || typeof window === 'undefined') return

        started = true
        needsMeasure = true
        lastTime = 0

        motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
        state.reducedMotion = motionQuery.matches
        motionQuery.addEventListener('change', onMotionChange)

        window.addEventListener('resize', requestMeasure)
        window.addEventListener('pointermove', onPointerMove, { passive: true })
        document.documentElement.addEventListener('pointerleave', onPointerLeave)

        resizeObserver = new ResizeObserver(requestMeasure)
        resizeObserver.observe(document.body)

        if (document.fonts) {
            document.fonts.ready.then(requestMeasure).catch(() => undefined)
        }

        rafId = requestAnimationFrame(tick)
    },

    stop(): void {
        if (!started) return

        started = false
        cancelAnimationFrame(rafId)

        motionQuery?.removeEventListener('change', onMotionChange)
        window.removeEventListener('resize', requestMeasure)
        window.removeEventListener('pointermove', onPointerMove)
        document.documentElement.removeEventListener('pointerleave', onPointerLeave)

        resizeObserver?.disconnect()
        resizeObserver = null
    },

    setLenis(instance: Lenis | null): void {
        lenis = instance
        needsMeasure = true
    },

    getLenis(): Lenis | null {
        return lenis
    },

    subscribe(callback: FrameCallback, priority = 0): () => void {
        const entry: Subscriber = { callback, priority }
        let index = subscribers.findIndex(item => item.priority > priority)

        if (index === -1) index = subscribers.length

        subscribers.splice(index, 0, entry)

        return () => {
            const position = subscribers.indexOf(entry)

            if (position !== -1) subscribers.splice(position, 1)
        }
    },

    onMeasure(callback: MeasureCallback): () => void {
        measurers.add(callback)
        needsMeasure = true

        return () => {
            measurers.delete(callback)
        }
    },

    requestMeasure,

    scrollTo(target: number | HTMLElement, options: ScrollOptions = {}): void {
        const { offset = 0, duration = 2.2, immediate = false, force = false, onComplete } = options

        if (lenis) {
            lenis.scrollTo(target, {
                offset,
                duration: state.reducedMotion ? 0 : duration,
                immediate: immediate || state.reducedMotion,
                force,
                easing: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
                onComplete: onComplete ? () => onComplete() : undefined
            })

            return
        }

        const top = typeof target === 'number' ? target : target.getBoundingClientRect().top + window.scrollY

        window.scrollTo({ top: top + offset, behavior: immediate ? 'auto' : 'smooth' })
        onComplete?.()
    }
}
